const MAX_FILE_SIZE = 1024 * 1024 * 500 // 500 MB
const MAX_EXTENSION_LENGTH = 10 // Maximum allowed file extension length

function intDiv(a: number, b: number): number {
  const result = a / b
  if (result >= 0) {
    return Math.floor(result)
  } else {
    return Math.ceil(result)
  }
}

function formatSize(size: number): string {
  // We really only need the first 4 or so, but doesn't hurt to add the rest
  const sizeSuffixes = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  let s = size
  let n = intDiv(s, 1024)
  let i = 0
  while (n > 0) {
    s = n
    n = intDiv(n, 1024)
    i += 1
  }

  return `${s}${sizeSuffixes[i]}`
}

/**
 * Throws when the file is larger than the upload limit.
 *
 * @param file
 */
export function assertValidFileSize(file: File) {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(
      `File "${file.name}" is too large at ${formatSize(
        file.size,
      )}. Max file size is ${formatSize(MAX_FILE_SIZE)}.`,
    )
  }
}

/**
 * Checks if a filename has a valid file extension
 */
function hasFileExtension(filename: string): boolean {
  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return false
  }
  const extension = filename.substring(lastDotIndex + 1)
  return (
    extension.length > 0 &&
    extension.length <= MAX_EXTENSION_LENGTH &&
    /^[a-zA-Z0-9]+$/.test(extension)
  )
}

/**
 * Validates that a file has an extension
 * @throws Error if file doesn't have a valid extension
 */
export function validateFileExtension(file: File): void {
  if (!hasFileExtension(file.name)) {
    throw new Error(
      `File "${file.name}" must have a file extension (e.g., .pdf, .jpg, .docx)`,
    )
  }
}

/**
 * Warns when the file has no MIME type.
 *
 * @param file
 */
export function assertValidFileType(file: File) {
  if (!file.type) {
    // Files from proprietary tax software often have no MIME type, so they
    // are let through.
    console.warn(`File "${file.name}" is of an unsupported type`)
  }
}

/**
 * Saves the data to the user's device under the given file name.
 *
 * @param data
 * @param fileName
 */
export function saveFile(data: Blob, fileName: string) {
  const url = URL.createObjectURL(data)
  const link = document.createElement('a')
  link.style.display = 'none'
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
  link.remove()
}
